import "./ReservationSidebar.scss"

import { FC, useMemo, useState, useEffect, useCallback } from "react"
import { useTranslation } from "react-i18next"
import { Dayjs } from "utils/dayjs"
import { uniq } from "lodash"

import Text from "components/Text/Text"
import { getPurposeLabel } from "utils/utils"
import Modal from "components/Modal/Modal"
import { ErrorCode, Purpose, UserPermission } from "config/constants"
import AssetService from "services/AssetService"
import { Guid } from "@/types"
import toastMessages from "stores/toastMessages"
import CustomError from "models/CustomError"
import useReservation from "hooks/useReservation"
import Status, { StatusType } from "components/Status/Status"
import { useTenantSettings } from "stores/tenantSettings"
import Tabs from "components/Tabs/Tabs"
import TabsItem from "components/Tabs/TabsItem/TabsItem"
import ReservationSidebarActionButtons from "components/ReservationSidebar/ReservationSidebarActionButtons"
import ReservationSidebarModals from "./ReservationSidebarModals"
import ReservationStatusTag from "../ReservationStatusTag/ReservationStatusTag"
import NoValue from "components/NoValue/NoValue"
import ReservationSidebarShipping from "./ReservationSidebarShipping"
import useTotalPrices from "pages/ReservationPage/steps/Pricing/hooks/useTotalPrices"
import ReservationSidebarInvoices from "./ReservationSidebarInvoices"
import { Grid, GridItem } from "components/Grid"
import CopyToClipboard from "components/CopyToClipboard/CopyToClipboard"
import useTenantLanguages from "hooks/useTenantLanguages"
import Reservation, { ReservationStatus } from "models/Reservation"
import ReservationSidebarDetails, { IReservationSidebarDetailsProps } from "./ReservationSidebarDetails"
import Button from "components/Button/Button"
import { Flex, FlexItem } from "components/Flex"
import Spinner from "components/Spinner/Spinner"
import reservationSidebars from "stores/reservationSidebars"
import ReservationSidebarConfiguration from "./ReservationSidebarConfiguration"
import useProfitCenters from "hooks/useProfitCenters"
import ReservationDocuments from "components/ReservationDocuments/ReservationDocuments"
import { usePermissions } from "stores/permissions"
import useReservationActions from "hooks/useReservationActions"
import { handleInvoiceBlockedError } from "utils/handleInvoiceBlockedError"
import useUserEmails from "hooks/useUserEmails"
import FlexibleContent from "components/FlexibleContent/FlexibleContent"


export enum ReservationSidebarTab {
    overview = 0,
    configuration,
    shipping,
    invoices,
    attachments,
}

export enum ReservationSidebarAttachementsTab {
    documents = 0,
}

export interface IReservationSidebarOnCloseParams {
    hasNoteBeenCreated?: boolean
}

export interface IReservationSidebarProps {
    reservationId: Guid
    initTabIndex?: ReservationSidebarTab
    initTabSubIndex?: ReservationSidebarAttachementsTab
    viewOnly?: boolean
    onClose?(params?: IReservationSidebarOnCloseParams): void
    onDelete?(): void
}

const ReservationSidebar: FC<React.PropsWithChildren<IReservationSidebarProps>> = ({
    reservationId,
    initTabIndex: initTabIndexProp = ReservationSidebarTab.overview,
    initTabSubIndex,
    viewOnly,
    onClose,
    onDelete,
}) => {
    const { t } = useTranslation()

    const [hasNoteBeenCreated, setHasNoteBeenCreated] = useState(false)
    const {
        tenantWarehouses,
        fetchTenantWarehouses,
        tenantEmployees,
        fetchTenantEmployees,
        deliveryTerms,
        fetchDeliveryTerms,
        paymentTerms,
        fetchPaymentTerms,
        orderTypes,
        fetchOrderTypes,
        countriesWithInternalCode,
        fetchCountriesWithInternalCode,
    } = useTenantSettings()
    const { reservation, refetchReservation } = useReservation({
        reservationId,
        withPricingDetails: true,
        hydrateConfiguration: true,
        hydrateInvoicePositions: true
    }, onClose)
    const { tenantLanguages } = useTenantLanguages()
    const { hasPermission } = usePermissions()
    const [initTabIndex, setInitTabIndex] = useState(initTabIndexProp)
    const [showDeleteConfirm, setShowDeleteConfirm] = useState(false)
    const [isDeleteInProgress, setIsDeleteInProgress] = useState(false)
    const [showReopenConfirm, setShowReopenConfirm] = useState(false)
    const [isReopenInProgress, setIsReopenInProgress] = useState(false)
    const [errorCode, setErrorCode] = useState<ErrorCode | null>(null)
    const [errorEmail, setErrorEmail] = useState<string>("")
    const [errorDate, setErrorDate] = useState<Dayjs | null>(null)
    const [isConfigurationTabInitialized, setIsConfigurationTabInitialized] = useState(false)
    const [isInvoicesTabInitialized, setIsInvoicesTabInitialized] = useState(false)
    const [isShippingTabInitialized, setIsShippingTabInitialized] = useState(false)
    const [isDocumentsTabInitialized, setIsDocumentsTabInitialized] = useState(false)
    const hasInvoicesTab = hasPermission(UserPermission.OutgoingInvoicesView) && !!reservation?.invoicing

    const {
        totalPrices
    } = useTotalPrices({
        reservationInvoicePositions: reservation?.pricingDetails?.invoicePositions || [],
        purpose: reservation?.purpose,
    })
    const { canEdit, canDelete, canReopen, canCopyAsReservation, canCopyAsOffer } = useReservationActions({
        status: reservation?.status,
        viewOnly,
        linkDelete: reservation?.linkDelete,
        linkReopen: reservation?.linkReopen,
    })

    const { reservation: rentToBuyRelatedReservation, isLoading: isLoadingRentToBuyRelatedReservation } = useReservation({ reservationId: reservation?.rentToBuy.relatedReservationId })
    const { tenantProfitCenters: profitCenters } = useProfitCenters()

    const reservationCreateChangeUserIds = useMemo(() => {
        if (!reservation) return []
        return uniq([
            reservation.createdBy,
            reservation.changedBy,
        ])
    }, [reservation])
    const { getEmail } = useUserEmails({ userIds: reservationCreateChangeUserIds })
    const createdByEmail = getEmail(reservation?.createdBy)
    const changedByEmail = getEmail(reservation?.changedBy)

    const handleCommentSave = useCallback(async (comment: string) => {
        if (!reservation || reservation.comment == comment) return

        try {
            if (reservation.linkUpdateComment) {
                AssetService.updateReservationComment(reservation.linkUpdateComment, comment)
            }

            toastMessages.showDataSaved()
            reservation.comment = comment
        } catch (error) {
            toastMessages.showSystemError()
        }
    }, [reservation, refetchReservation, t])

    const reservationDetails: IReservationSidebarDetailsProps | undefined = useMemo(() => {
        if (!reservation) {
            return
        }

        const language = tenantLanguages?.find(lang => lang.isoCode === reservation.language) ?? undefined

        const addressWarehouseDescription = (() => {
            if (reservation.purpose !== Purpose.relocation) return
            if (!tenantWarehouses) return null
            return tenantWarehouses?.getWarehouseByCoordinates(reservation.location, 10)?.description || undefined
        })()

        const warehouseDescription = (() => {
            if (!reservation.warehouseId) return
            if (!tenantWarehouses) return null
            if (tenantWarehouses?.noWarehouseId && reservation.warehouseId === tenantWarehouses.noWarehouseId) {
                return t("common.noWarehouse")
            }
            return tenantWarehouses?.entities?.find(wh => wh.id === reservation.warehouseId)?.description ?? undefined
        })()

        const salesPerson = reservation.salesPersonNumber && !tenantEmployees ? null : (tenantEmployees && tenantEmployees.entities.length > 0 && reservation.salesPersonNumber
            ? tenantEmployees.entities.find(e => e.number === reservation.salesPersonNumber)
            : undefined)

        const salesAdmin = reservation.salesAdminNumber && !tenantEmployees ? null : (tenantEmployees && tenantEmployees.entities.length > 0 && reservation.salesAdminNumber
            ? tenantEmployees.entities.find(e => e.number === reservation.salesAdminNumber)
            : undefined)

        const deliveryTerm = reservation.deliveryTermId && !deliveryTerms ? null : (deliveryTerms && deliveryTerms.entities.length > 0 && reservation.deliveryTermId
            ? deliveryTerms.entities.find(dt => dt.termId === reservation.deliveryTermId)
            : undefined)

        const paymentTerm = reservation.pricingDetails?.termsOfPaymentCode && !paymentTerms ? null : (paymentTerms && paymentTerms.length > 0 && reservation.pricingDetails?.termsOfPaymentCode
            ? paymentTerms.find(PageTransitionEvent => PageTransitionEvent.code === reservation.pricingDetails?.termsOfPaymentCode)
            : undefined)

        const orderType = reservation.pricingDetails?.orderTypeId && !orderTypes ? null : (orderTypes && orderTypes.entities.length > 0 && reservation.pricingDetails?.orderTypeId
            ? orderTypes.entities.find(ot => ot.id === reservation.pricingDetails?.orderTypeId)
            : undefined)

        const originCountry = reservation.pricingDetails?.originCountryCode && !countriesWithInternalCode ? null : (countriesWithInternalCode && countriesWithInternalCode.length > 0 && reservation.pricingDetails?.originCountryCode
            ? countriesWithInternalCode.find(ot => ot.isoNumericCode === reservation.pricingDetails?.originCountryCode)
            : undefined)

        const targetCountry = reservation.targetCountryCode && !countriesWithInternalCode ? null : (countriesWithInternalCode && countriesWithInternalCode.length > 0 && reservation.targetCountryCode
            ? countriesWithInternalCode.find(ot => ot.isoNumericCode === reservation.targetCountryCode)
            : undefined)

        const profitCenter = reservation.profitCenterId && !profitCenters ? null : (profitCenters && profitCenters.items.length > 0 && reservation.profitCenterId
            ? profitCenters.items.find(pc => pc.id === reservation.profitCenterId)
            : undefined)

        return {
            reservation,
            rentToBuyRelatedReservation,
            isLoadingRentToBuyRelatedReservation,
            language,
            addressWarehouseDescription,
            warehouseDescription: warehouseDescription ? warehouseDescription : (warehouseDescription === null ? <NoValue>{t("common.loading")}</NoValue> : undefined),
            salesPerson,
            salesAdmin,
            deliveryTerm,
            paymentTerm,
            orderType,
            originCountry,
            targetCountry,
            totalPrices,
            profitCenter,
            canEdit,
            handleCommentSave,
        }
    }, [reservation, tenantLanguages, tenantEmployees, deliveryTerms, paymentTerms, orderTypes, countriesWithInternalCode,
        profitCenters, rentToBuyRelatedReservation, isLoadingRentToBuyRelatedReservation, t, totalPrices, canEdit,
        tenantWarehouses])

    useEffect(() => {
        if (!reservation?.salesAdminNumber && !reservation?.salesPersonNumber) return
        (async () => {
            try {
                await fetchTenantEmployees()
            } catch (e) {
                toastMessages.showSystemError()
            }
        })()
    }, [fetchTenantEmployees, reservation?.salesAdminNumber, reservation?.salesPersonNumber])

    useEffect(() => {
        if (!reservation?.warehouseId) return
        (async () => {
            try {
                await fetchTenantWarehouses()
            } catch (e) {
                toastMessages.showSystemError()
            }
        })()
    }, [fetchTenantWarehouses, reservation?.warehouseId])

    useEffect(() => {
        if (!reservation?.deliveryTermId) return
        (async () => {
            try {
                await fetchDeliveryTerms()
            } catch (e) {
                toastMessages.showSystemError()
            }
        })()
    }, [fetchDeliveryTerms, reservation?.deliveryTermId])

    useEffect(() => {
        if (!reservation?.pricingDetails?.termsOfPaymentCode) return
        (async () => {
            try {
                await fetchPaymentTerms()
            } catch (e) {
                toastMessages.showSystemError()
            }
        })()
    }, [fetchPaymentTerms, reservation?.pricingDetails?.termsOfPaymentCode])

    useEffect(() => {
        if (!reservation?.pricingDetails?.orderTypeId) return
        (async () => {
            try {
                await fetchOrderTypes()
            } catch (e) {
                toastMessages.showSystemError()
            }
        })()
    }, [fetchOrderTypes, reservation?.pricingDetails?.orderTypeId])

    useEffect(() => {
        if (!reservation?.pricingDetails?.originCountryCode && !reservation?.targetCountryCode) return
        (async () => {
            try {
                await fetchCountriesWithInternalCode()
            } catch (e) {
                toastMessages.showSystemError()
            }
        })()
    }, [fetchCountriesWithInternalCode, reservation?.pricingDetails?.originCountryCode, reservation?.targetCountryCode])

    function resetError() {
        setErrorCode(null)
        setErrorEmail("")
        setErrorDate(null)
    }

    const onDeleteClick = useCallback(() => {
        resetError()
        setShowDeleteConfirm(true)
    }, [])

    async function onDeleteCancel() {
        setShowDeleteConfirm(false)
        resetError()
    }

    async function onDeleteConfirm(overrideOwnership?: boolean) {
        if (!reservation?.linkDelete) {
            toastMessages.showSystemError()
            return
        }
        setIsDeleteInProgress(true)
        try {
            await AssetService.deleteReservation(reservation.linkDelete, overrideOwnership)
            resetError()
            setShowDeleteConfirm(false)
            setIsDeleteInProgress(false)

            toastMessages.showDataDeleted()

            if (onDelete) {
                onDelete()
            }
            if (onClose) {
                onClose()
            }
        } catch (e) {
            setIsDeleteInProgress(false)

            if (e instanceof CustomError) {
                const error: CustomError<{ email: string, date: Dayjs | null, readOnly?: boolean } | undefined> = e

                setErrorCode(error.code || null)
                setErrorEmail(error.payload?.email || "")
                setErrorDate(error.payload?.date || null)

                if (error.code !== ErrorCode.conflict
                    && error.code !== ErrorCode.forbidden) {
                    toastMessages.showSystemError()
                } else {
                    setShowDeleteConfirm(false)
                }
            } else {
                toastMessages.showSystemError()
            }
        }
    }


    const onReopenClick = useCallback(() => {
        setShowReopenConfirm(true)
    }, [])

    async function onReopenCancel() {
        setShowReopenConfirm(false)
    }

    async function onReopenConfirm() {
        if (!reservation?.linkReopen) {
            toastMessages.showSystemError()
            return
        }
        setIsReopenInProgress(true)
        try {
            await AssetService.reopenReservation(reservation.linkReopen)
            setShowReopenConfirm(false)
            setIsReopenInProgress(false)
            refetchReservation()
        } catch (e) {
            setIsReopenInProgress(false)
            toastMessages.showSystemError()
        }
    }

    function onTabSwitch(tabIndex: ReservationSidebarTab, subIndex?: number) {
        switch (tabIndex) {
            case ReservationSidebarTab.configuration:
                setIsConfigurationTabInitialized(true)
                break
            case ReservationSidebarTab.shipping:
                setIsShippingTabInitialized(true)
                break
            case ReservationSidebarTab.invoices:
                setIsInvoicesTabInitialized(true)
                break
            case ReservationSidebarTab.attachments:
                if (subIndex === ReservationSidebarAttachementsTab.documents) {
                    setIsDocumentsTabInitialized(true)
                }
                break
        }
    }

    useEffect(() => {
        if (initTabIndex) {
            onTabSwitch(initTabIndex, initTabSubIndex)
        }
    }, [initTabIndex, initTabSubIndex])

    async function onShippingOrderChange() {
        await refetchReservation()
        setInitTabIndex(ReservationSidebarTab.shipping)
    }

    async function onNoteCreated() {
        setHasNoteBeenCreated(true)
    }

    async function onInvoiceBlocked() {
        const currentReservation = await refetchReservation()
        if (currentReservation) {
            await handleInvoiceBlockedError({ reservation: currentReservation })
        }
    }

    const handleClose = useCallback(() => {
        onClose?.({ hasNoteBeenCreated })
    }, [hasNoteBeenCreated, onClose])

    const toolbar = useMemo(() => {
        if (!reservation) return

        return <Grid columns="max-content max-content minmax(0, auto) "
            alignItems="center"
            className="w-100">
            {!!reservation.number &&
                <Text type="pSmallest">
                    <Grid
                        columns="max-content minmax(0, auto)"
                        gap="0.8rem"
                        alignItems="center">
                        <GridItem className="color-grey-500">
                            {t("reservation.requestNo")}:
                        </GridItem>
                        <GridItem>
                            <CopyToClipboard copyText={reservation.number}>
                                <FlexibleContent text={reservation.number} className="font-bold color-brand-blue" />
                            </CopyToClipboard>
                        </GridItem>
                    </Grid>
                </Text>
            }
            <GridItem>
                <ReservationStatusTag status={reservation.status} />
            </GridItem>
            <GridItem justifySelf="end">
                <ReservationSidebarActionButtons
                    reservationId={reservation.id}
                    reservationNumber={reservation.number}
                    canEarlyReturn={reservation.isEarlyReturnPossible}
                    canCopyAsReservation={canCopyAsReservation}
                    canCopyAsOffer={canCopyAsOffer}
                    canEdit={canEdit}
                    onDeleteClick={canDelete ? onDeleteClick : undefined}
                    onReopenClick={canReopen ? onReopenClick : undefined}
                    onCloseClick={handleClose}
                />
            </GridItem>
        </Grid>

    }, [reservation, t, canCopyAsReservation, canCopyAsOffer, canEdit, canDelete, onDeleteClick,
        canReopen, onReopenClick, handleClose])

    return (
        <>
            <Modal type="sidebar"
                maxWidth="720px"
                isBusy={!reservation}
                title={t('requestDetails.title')}
                onClose={handleClose}
                closeButton={false}
                toolbar={toolbar}>
                {!!reservation &&
                    <>
                        {!!reservation.disposalDate &&
                            <div className="mb-3">
                                <Status type={StatusType.caution}>
                                    <>{t("assetDisposal.disposalDate")} {reservation.disposalDate.format(t("config.dateLongFormat"))}</>
                                </Status>
                            </div>
                        }
                        <div className="mb-4">
                            <Text type="h2">
                                {t('requestDetails.requestForX', { purpose: getPurposeLabel(reservation.purpose) })}
                            </Text>
                            {isLoadingRentToBuyRelatedReservation
                                ? <Spinner size={24} className="mt-1" />
                                : (rentToBuyRelatedReservation
                                    ? <Flex alignItems="center" wrap="wrap" gap=".8rem" className="mt-1">
                                        <Button
                                            theme="link"
                                            label={`${t("reservation.relatedRequestForRentToBuy")} (${rentToBuyRelatedReservation.number})`}
                                            onClick={() => reservationSidebars.show({ reservationId: rentToBuyRelatedReservation.id })}
                                        />
                                        <ReservationStatusTag status={rentToBuyRelatedReservation.status} />
                                    </Flex>
                                    : null)
                            }
                        </div>
                        <Tabs
                            onSwitchTab={onTabSwitch}
                            defaultActiveIndex={initTabIndex}
                            defaultActiveSubIndex={initTabSubIndex}
                            data-testid="tabs">
                            <TabsItem label={t('requestDetails.overviewTab')}>
                                <Flex flow="column" gap="6.4rem">
                                    <FlexItem>
                                        {Reservation.isReservationStatusSameOrAfter(reservation.status, ReservationStatus.canceled) &&
                                            <Status
                                                type={StatusType.warning}>
                                                <strong>{t('reservation.cancelReason')}</strong> {reservation.cancellationReason}
                                            </Status>
                                        }
                                        {!!reservationDetails &&
                                            <ReservationSidebarDetails
                                                {...reservationDetails}
                                            />
                                        }
                                    </FlexItem>
                                    <FlexItem className="text-right color-grey-500">
                                        <Text type="pSmallest" className="font-bold">
                                            {t(createdByEmail ? "common.createdByOnX" : "common.createdOnX", {
                                                user: createdByEmail,
                                                date: reservation.created.format(t("config.dateLongTimeFormat"))
                                            })}
                                        </Text>
                                        {(changedByEmail !== createdByEmail || !reservation.changed.isSame(reservation.created)) &&
                                            <Text type="pSmallest">
                                                {t(createdByEmail ? "common.lastChangedByOnX" : "common.lastChangedOnX", {
                                                    user: changedByEmail,
                                                    date: reservation.changed.format(t("config.dateLongTimeFormat"))
                                                })}
                                            </Text>
                                        }
                                    </FlexItem>
                                </Flex>
                            </TabsItem>
                            <TabsItem label={t('requestDetails.configurationTab')}>
                                {isConfigurationTabInitialized &&
                                    <ReservationSidebarConfiguration
                                        reservationId={reservation.id}
                                        configurationCompleted={reservation.configurationCompleted || false}
                                        articles={reservation.articles || []}
                                        nodeArticleId={reservation.articleId}
                                        purpose={reservation.purpose}
                                        canEdit={canEdit}
                                        referenceNumbers={reservation.referenceNumbers}
                                    />
                                }
                            </TabsItem>
                            <TabsItem label={t('requestDetails.shippingTab')}>
                                {isShippingTabInitialized &&
                                    <ReservationSidebarShipping
                                        reservation={reservation}
                                        onShippingOrderChange={onShippingOrderChange}
                                        canEditReservation={canEdit}
                                    />
                                }
                            </TabsItem>
                            {hasInvoicesTab &&
                                <TabsItem label={t('requestDetails.invoicesTab')}>
                                    {isInvoicesTabInitialized &&
                                        <ReservationSidebarInvoices
                                            reservationId={reservation.id}
                                            currency={reservation.requestCurrency}
                                            purpose={reservation.purpose}
                                            disposalDate={reservation.disposalDate || null}
                                            onNoteCreated={onNoteCreated}
                                            onInvoiceBlocked={onInvoiceBlocked}
                                        />
                                    }
                                </TabsItem>
                            }
                            {!!reservation.linkGetReservationDocuments &&
                                <TabsItem label={t('common.attachments')}
                                    subItemsAlign="right"
                                    subItemsWidth="22rem">
                                    <TabsItem label={t('common.documents')}>
                                        {isDocumentsTabInitialized &&
                                            <ReservationDocuments
                                                reservationNumber={reservation.number}
                                                linkGetReservationDocuments={reservation.linkGetReservationDocuments}
                                                linkAddReservationDocuments={reservation.linkAddReservationDocuments}
                                            />
                                        }
                                    </TabsItem>
                                </TabsItem>
                            }
                        </Tabs>
                    </>
                }
            </Modal>
            <ReservationSidebarModals
                showDeleteConfirm={showDeleteConfirm}
                isDeleteInProgress={isDeleteInProgress}
                onDeleteConfirm={onDeleteConfirm}
                onDeleteCancel={onDeleteCancel}
                showReopenConfirm={showReopenConfirm}
                isReopenInProgress={isReopenInProgress}
                onReopenConfirm={onReopenConfirm}
                onReopenCancel={onReopenCancel}
                showReservationLockedByAnotherUserError={errorCode === ErrorCode.conflict}
                showReservationUpdatedByAnotherUserError={errorCode === ErrorCode.forbidden}
                errorEmail={errorEmail}
                errorDate={errorDate}
                onResetError={resetError}
            />
        </>
    )
}

export default ReservationSidebar
